import './tech-passport-preview-provider.scss';

import { Alert, Image } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/Shared/ui';
import { useWindowSize } from '@/hooks/useWindowSize';
import { downloadFilesApi } from '@/integrations/index.api';
import FileTextIcon from '@/media/icons/file-text.svg?react';
import { handleError } from '@/services/handleError';
import { useAppSelector } from '@/store';

type TProps = {
  children: React.ReactNode;
  isPreviewVisible: boolean;
  onPreviewVisibleChange: (isVisible: boolean) => void;
};

const classNameBase = 'tech-passport-preview-provider';

const TechPassportPreviewProvider: React.FC<TProps> = ({
  children,
  isPreviewVisible,
  onPreviewVisibleChange
}) => {
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { attachedFiles } = useAppSelector((state) => state.financingStep);

  const windowSize = useWindowSize();
  const isSmallScreen = windowSize[0] < 756;

  const [isLoading, setIsLoading] = useState(false);
  const [dataMap, setDataMap] = useState<{
    base64: string;
    name: string;
    type: 'pdf' | 'image';
    uuid: string;
  } | null>();

  const techPassportUuid: string | null = useMemo(() => {
    if (!orderApp || !orderApp.isOnlineRequest) return null;
    const file = attachedFiles.find((file) => file.type === 'tech_passport');
    if (!file) return null;
    return file.uuid;
  }, [orderApp, attachedFiles]);

  const fetchAndOpenTechPassport = async () => {
    if (!techPassportUuid) return;

    if (dataMap?.uuid === techPassportUuid) {
      onPreviewVisibleChange(true);
      return;
    }

    setIsLoading(true);
    setDataMap(null);
    try {
      const response =
        await downloadFilesApi.downloadBankFile(techPassportUuid);
      setDataMap({
        base64: response.base64,
        name: response.name,
        type: response.name.endsWith('.pdf') ? 'pdf' : 'image',
        uuid: techPassportUuid
      });
      onPreviewVisibleChange(true);
    } catch (error) {
      handleError(error, {
        customTitle: 'Не удалось загрузить технический паспорт',
        onError: () => {
          setDataMap(null);
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSmallScreen) {
      onPreviewVisibleChange(false);
    }
  }, [isSmallScreen]);

  if (!techPassportUuid) return children;
  if (isSmallScreen)
    return (
      <div>
        <Alert
          title="Тех. паспорт не доступен для просмотра на небольших экранах"
          type="warning"
          showIcon
        />
        {children}
      </div>
    );
  return (
    <div className={classNameBase}>
      <div className={`${classNameBase}__form-container`}>
        <Button
          type="button"
          size="small"
          onClick={() => {
            if (isPreviewVisible) {
              onPreviewVisibleChange(false);
            } else {
              fetchAndOpenTechPassport();
            }
          }}
          isLoading={isLoading}
        >
          <FileTextIcon className={`${classNameBase}__icon`} />
          {isPreviewVisible ? 'Скрыть тех. паспорт' : 'Открыть тех. паспорт'}
        </Button>
        {children}
      </div>
      {isPreviewVisible && !!dataMap && (
        <div className={`${classNameBase}__preview-container`}>
          {dataMap.type === 'pdf' ? (
            <iframe
              src={`data:application/pdf;base64,${dataMap.base64}`}
              className={`${classNameBase}__iframe`}
            />
          ) : (
            <Image
              alt={dataMap.name}
              src={`data:image/webp;base64,${dataMap.base64}`}
              rootClassName={`${classNameBase}__image-root`}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default TechPassportPreviewProvider;
